import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client.js'
import { formatDate, formatDateTime } from '../lib/format.js'

interface AuditEntry {
  id: string
  action: string
  payload: Record<string, unknown> | null
  createdAt: string
  actorName: string | null
  actorLabel: string | null
}

/** Plain German for every action the log records. Unknown ones fall back to a neutral text. */
const ACTION_LABEL: Record<string, string> = {
  'task.created_adhoc': 'Sonderaufgabe angelegt',
  'task.updated': 'Aufgabe geändert',
  'task.reassigned': 'Neu zugewiesen',
  'task.completed': 'In der App erledigt',
  'task.recorded_by_backoffice': 'Von der Verwaltung nachgetragen',
  'task.final_missed': 'Endgültig nicht erledigt',
  'task.deleted': 'Gelöscht',
  'task.imported': 'Importiert',
  'task.bulk_assign': 'Neu zugewiesen (Mehrfachauswahl)',
  'task.bulk_move': 'Verschoben (Mehrfachauswahl)',
  'task.bulk_skip': 'Entfällt (Mehrfachauswahl)',
  'quality.bestaetigt': 'Qualitätshinweis bestätigt',
  'quality.verworfen': 'Qualitätshinweis verworfen',
  'handover.remuneration_exported': 'Für die Vergütung exportiert',
  'handover.youtilly_reported': 'An Youtilly gemeldet',
  'report.single_proof': 'Einzelnachweis erstellt',
  'object.created': 'Angelegt',
  'object.updated': 'Geändert',
  'object.moved': 'Verschoben',
  'object.archived': 'Archiviert',
  'object.restored': 'Wiederhergestellt',
  'object.blocked': 'Gesperrt',
  'object.unblocked': 'Sperre aufgehoben',
  'object.imported': 'Importiert',
  'board.created': 'Aushang erstellt',
  'board.renewed': 'Aushang erneuert',
  'board.removed': 'Aushang entfernt',
  'document.uploaded': 'Dokument hochgeladen',
  'document.updated': 'Dokument geändert',
  'document.deleted': 'Dokument gelöscht',
  'report.link_created': 'Nachweis-Link erstellt',
  'report.link_revoked': 'Nachweis-Link widerrufen',
  'report.proof_generated': 'Nachweis erstellt',
  'portal.proof_viewed': 'Nachweis im Kundenportal angesehen',
}

const STATUS_LABEL: Record<string, string> = {
  open: 'offen',
  done: 'erledigt',
  missed: 'überfällig',
  skipped: 'entfällt',
  final_missed: 'endgültig nicht erledigt',
}

/** One short line on what changed, from the fields people actually care about. */
function detail(entry: AuditEntry): string | null {
  const p = entry.payload ?? {}
  const after = (p.after ?? p) as Record<string, unknown>
  const parts: string[] = []
  if (typeof after.status === 'string') parts.push(`Status ${STATUS_LABEL[after.status] ?? after.status}`)
  if (typeof after.dueDate === 'string' && entry.action !== 'task.deleted') parts.push(`fällig am ${formatDate(after.dueDate)}`)
  if ('assigneeId' in after || 'assigneeTeamId' in after) parts.push('Zuständigkeit geändert')
  if ('note' in after && entry.action === 'task.updated') parts.push('Hinweis geändert')
  if ('externalRef' in after) parts.push('Youtilly-ID geändert')
  if (typeof p.reason === 'string') parts.push(p.reason)
  if (typeof p.name === 'string' && /^(object|document)\./.test(entry.action)) parts.push(p.name)
  if (typeof p.share === 'boolean' && entry.action !== 'document.deleted') parts.push(p.share ? 'für den Kunden freigegeben' : 'nur intern')
  if (typeof p.count === 'number' && p.count > 1) parts.push(`${p.count} Aufgaben`)
  if (typeof p.batch === 'number' && p.batch > 1) parts.push(`zusammen mit ${p.batch - 1} weiteren`)
  return parts.length > 0 ? parts.join(', ') : null
}

/**
 * Änderungsprotokoll for one task or one object: who did what, when. The
 * audit log is written for every change anyway; this is where it becomes
 * readable, so "wer hat das verschoben?" is answered without asking around.
 */
export function History({ entity, entityId }: { entity: 'task_occurrence' | 'object'; entityId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['audit', entity, entityId],
    queryFn: () => api<AuditEntry[]>(`/api/audit?entity=${entity}&entityId=${entityId}`),
  })
  if (isLoading) return <p className="muted small">Wird geladen …</p>
  if (!data || data.length === 0) return <p className="muted small">Noch keine Einträge.</p>
  return (
    <ol className="history">
      {data.map((entry) => {
        const extra = detail(entry)
        return (
          <li key={entry.id}>
            <div className="history-when">{formatDateTime(entry.createdAt)}</div>
            <div>
              <span className="strong">{ACTION_LABEL[entry.action] ?? 'Änderung'}</span>
              <span className="muted">, {entry.actorName ?? entry.actorLabel ?? 'System'}</span>
              {extra && <div className="muted small">{extra}</div>}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
